import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';
import { Button } from '@/components/ui/button';
import { FloatingLabelInput } from '@/components/ui/floating-label-input';
import { Form, FormField } from '@/components/ui/form';
import { useAuthStore } from '@/features/authentication/store/authStore';
import { loginSchema, type LoginFormData } from '../schemas/loginSchema';

export function LoginForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error: storeError, clearError } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const redirectTo = (location.state as { from?: string })?.from || '/';

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      usernameOrEmail: '',
      password: '',
    },
  });

  const displayError = localError || storeError;

  const onSubmit = async (data: LoginFormData) => {
    setLocalError(null);
    clearError();

    try {
      await login(data.usernameOrEmail, data.password);
      // Check store state after login — redirect to change password if required
      if (useAuthStore.getState().mustChangePassword) {
        navigate('/change-password', { replace: true });
      } else {
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      const message = getApiErrorMessage(err, 'An unexpected error occurred');
      setLocalError(message);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {displayError && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {displayError}
          </div>
        )}

        <FormField
          control={form.control}
          name="usernameOrEmail"
          render={({ field, fieldState }) => (
            <div>
              <FloatingLabelInput
                id="usernameOrEmail"
                label="Username or Email"
                autoComplete="username"
                error={!!fieldState.error}
                {...field}
              />
              {fieldState.error && (
                <p className="mt-1.5 text-xs text-destructive">
                  {fieldState.error.message}
                </p>
              )}
            </div>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field, fieldState }) => (
            <div>
              <FloatingLabelInput
                id="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                error={!!fieldState.error}
                trailing={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                }
                {...field}
              />
              {fieldState.error && (
                <p className="mt-1.5 text-xs text-destructive">
                  {fieldState.error.message}
                </p>
              )}
            </div>
          )}
        />

        <Button
          type="submit"
          className="w-full"
          isLoading={isLoading || form.formState.isSubmitting}
        >
          Sign In
        </Button>
      </form>
    </Form>
  );
}
